import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // HTTP 0 = request was cancelled/aborted by the browser (e.g. navigation,
      // tab close, Fetch API abort on Safari/iOS). These are NOT real errors —
      // silently propagate without showing toast.
      if (error.status === 0) {
        return throwError(() => error);
      }

      console.error('[ErrorInterceptor] Fired with error:', error);

      // ─── 401 Unauthorized: clear session and redirect to login ───────────
      if (error.status === 401) {
        const isLogoutRequest = req.url.includes('/auth/logout');
        if (!isLogoutRequest) {
          const authService = injector.get(AuthService);
          const router = injector.get(Router);
          // Clear local session state without calling API (cookie already invalid)
          authService.clearSession();
          router.navigate(['/login'], { replaceUrl: true });
        }
        return throwError(() => error);
      }

      const translateService = injector.get(TranslateService);
      let errorMessageDetail = '';

      if (error.error instanceof ErrorEvent) {
        // Client-side or network error
        errorMessageDetail = error.error.message;
      } else if (error.status === 413) {
        // Handle Cloudflare/Nginx/API 413 Content Too Large
        errorMessageDetail = translateService.instant('ERRORS.PAYLOAD_TOO_LARGE');
      } else if (error.status === 404) {
        // Handle 404 Not Found globally
        errorMessageDetail = translateService.instant('ERRORS.NOT_FOUND');
      } else {
        // Backend returns unsuccessful response code
        if (error.error && error.error.errorCode) {
          const errorCode = error.error.errorCode;
          const translationKey = `ERRORS.${errorCode}`;
          const translatedMessage = translateService.instant(translationKey);
          if (translatedMessage === translationKey) {
             // No i18n key found — use generic translated error instead of raw backend message
             errorMessageDetail = translateService.instant('ERRORS.INTERNAL_ERROR');
          } else {
             errorMessageDetail = translatedMessage;
          }
        }
        else {
           errorMessageDetail = translateService.instant('ERRORS.INTERNAL_ERROR');
        }
      }

      // Display the toast message globally
      messageService.add({
        severity: 'error',
        summary: translateService.instant('MESSAGES.ERROR'),
        detail: errorMessageDetail,
        life: 5000
      });

      return throwError(() => error);
    })
  );
};
